import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  actionsColumn,
  dateColumn,
  statusColumn,
  type ColumnDef,
} from '@/components/ui'
import { customerApi } from '../lib/api'

interface DocRow {
  DocEntry: number
  DocNum?: number
  DocDate?: string
  DocDueDate?: string
  CardCode: string
  CardName?: string
  DocTotal?: number
  DocCurrency?: string
  DocumentStatus?: string
}

interface Props {
  title: string
  description?: string
  endpoint: '/sap/quotations' | '/sap/orders' | '/sap/deliveries' | '/sap/invoices' | '/sap/credits'
  detailBase: string
}

function formatMoney(n?: number, cur?: string) {
  if (n == null) return '—'
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur ?? 'EUR'}`
}

export function CustomerDocumentsListPage({ title, description, endpoint, detailBase }: Props) {
  const [rows, setRows] = useState<DocRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [debugData, setDebugData] = useState<unknown>(null)
  const [debugLoading, setDebugLoading] = useState(false)
  const [debugError, setDebugError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    setError(null)
    setErrorCode(null)
    setDebugData(null)
    setDebugError(null)
    customerApi.get<{ data: DocRow[] }>(endpoint)
      .then((res) => setRows(res.data?.data ?? []))
      .catch((e: Error & { backendCode?: string }) => {
        setError(e.message)
        setErrorCode(e.backendCode ?? null)
      })
      .finally(() => setLoading(false))
  }, [endpoint])

  const linkMissing =
    errorCode === 'CUSTOMER_NO_SAP_LINK' ||
    errorCode === 'CUSTOMER_NO_TANSS_LINK' ||
    errorCode === 'CUSTOMER_NO_TANSS_NUMBER' ||
    errorCode === 'CUSTOMER_NO_TANSS_CREDENTIALS' ||
    errorCode === 'CUSTOMER_NO_TENANT'

  const loadDebug = () => {
    setDebugLoading(true)
    setDebugError(null)
    customerApi.get<unknown>('/sap/link-debug')
      .then((res) => setDebugData(res.data))
      .catch((e: Error) => setDebugError(e.message))
      .finally(() => setDebugLoading(false))
  }

  const columns = useMemo<ColumnDef<DocRow, unknown>[]>(
    () => [
      {
        accessorKey: 'DocNum',
        header: 'Nr.',
        cell: ({ row }) => row.original.DocNum ?? row.original.DocEntry,
      },
      dateColumn<DocRow>('DocDate', 'Datum'),
      statusColumn<DocRow>('DocumentStatus'),
      {
        accessorKey: 'DocTotal',
        header: 'Betrag',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {formatMoney(row.original.DocTotal, row.original.DocCurrency)}
          </span>
        ),
      },
      actionsColumn<DocRow>('', (row) => (
        <Button variant="link" size="sm" onClick={() => navigate(`${detailBase}/${row.DocEntry}`)}>
          Details
        </Button>
      )),
    ],
    [detailBase, navigate],
  )

  return (
    <div>
      <PageHeader title={title} description={description} />
      {loading && <LoadingState />}
      {error && (
        <ErrorState
          title={linkMissing ? 'Kein SAP-Konto verknüpft' : 'Fehler beim Laden'}
          description={
            linkMissing
              ? 'Dein Benutzerkonto ist noch keinem SAP-Kunden zugeordnet. Bitte wende dich an deinen Ansprechpartner bei implec, damit dein Zugang freigeschaltet wird.'
              : undefined
          }
          details={error}
        />
      )}
      {error && linkMissing && (
        <div className="mt-4 mx-auto max-w-3xl">
          <div className="flex items-center gap-2 justify-center">
            <Button variant="secondary" size="sm" onClick={loadDebug} disabled={debugLoading}>
              {debugLoading ? 'Lade TANSS-Diagnose…' : 'TANSS-Rohdaten anzeigen (Admin-Diagnose)'}
            </Button>
          </div>
          {debugError && (
            <pre className="mt-3 whitespace-pre-wrap break-words rounded bg-red-50 border border-red-200 p-3 text-xs font-mono text-red-700">
              {debugError}
            </pre>
          )}
          {debugData != null && (
            <pre className="mt-3 whitespace-pre-wrap break-words rounded bg-gray-50 border border-gray-200 p-3 text-xs font-mono text-gray-800 max-h-[70vh] overflow-auto">
              {JSON.stringify(debugData, null, 2)}
            </pre>
          )}
        </div>
      )}
      {!loading && !error && (
        <Card>
          <Card.Body>
            <DataTable<DocRow>
              data={rows}
              columns={columns}
              getRowId={(r) => String(r.DocEntry)}
              emptyState={<EmptyState title="Keine Einträge" description="Zu diesem Bereich liegen aktuell keine Dokumente vor." />}
            />
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
